import {
  AppBar,
  Avatar,
  Box,
  ListItem,
  ListItemAvatar,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { Payment } from '@mui/icons-material';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { jwtDecode } from 'jwt-decode';
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Logo from '../assets/images/appLogos/logo.png';
import './AppHeader.style.css';
import { useLogoutMutation } from '../services/auth/auth.api';
import PATH from '../shared/constants/path.const';
import ApplicationLogoutAPI from '../pages/common/logout/ApplicationLogoutAPI';
import CustomButton from '../shared/components/CustomButton';
import useSessionCheck from '../shared/hooks/useSessionCheck';
import { isUserBoth, isUserClient } from '../shared/utils/utils';
import { getColors } from '../theme';

const colors = getColors();

function AppHeader({ drawopen, toggleDrawer, setLogoutCheckStatus }) {
  const isClient = isUserClient();
  const isBoth = isUserBoth();
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = localStorage.getItem('loggedInUser');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [logoutStatus, setLogoutStatus] = useState();
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [logoutUser] = useLogoutMutation();
  const { IDLE_TIME } = localStorage.getItem('token')
    ? jwtDecode(localStorage.getItem('token'))
    : { IDLE_TIME: 600 };
  const idleTime = IDLE_TIME || 600;

  const timer = useSessionCheck(idleTime, sessionDialogOpen, setSessionDialogOpen);

  const handleStay = () => {
    localStorage.setItem('stayClicked', Date.now().toString());
    setSessionDialogOpen(false);
  };

  useEffect(() => {
    if (timer <= 60 && timer > 0) {
      setSessionDialogOpen(true);
    }
    if (!timer) {
      setLogoutCheckStatus(true);
      localStorage.setItem('sessionExpired', 'true');
      ApplicationLogoutAPI(setLogoutStatus, logoutUser, dispatch, navigate);
    }
  }, [timer]);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const { colorShades, text, primary } = colors;

  function stringAvatar(name) {
    const parts = name.split(' ');
    const firstInitial = parts[0]?.charAt(0) || '';
    const secondInitial = parts[1]?.charAt(0) || '';
    return {
      sx: {
        bgcolor: primary.main,
        color: colorShades.pureWhite,
      },
      children: `${firstInitial}${secondInitial}`.toUpperCase(),
    };
  }

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    if (setLogoutCheckStatus && logoutStatus === 'logout successful') setLogoutCheckStatus(false);
  }, [logoutStatus]);

  const formatSecToMin = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <AppBar
      sx={{
        minHeight: 0,
        color: text.primary,
        backgroundColor: colorShades.pureWhite,
        zIndex: 999,
        boxShadow: 'none',
        position: 'inherit',
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          background: colorShades.pureWhite,
          display: 'flex',
          justifyContent: 'space-between',
          minHeight: '35px !important',
        }}
      >
        <Box display={{ xs: 'none' }} />

        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
          paddingLeft={{ xs: '20px', md: '16px' }}
        >
          {!isClient && (
            <IconButton
              onClick={toggleDrawer}
              sx={{
                color: drawopen ? primary.main : colorShades.greyShade,
                backgroundColor: 'transparent',
                borderRadius: '8px',
                height: '32px',
                width: '32px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: primary.veryLight,
                  color: primary.main,
                },
              }}
            >
              <MenuOutlinedIcon sx={{ fontSize: '22px' }} />
            </IconButton>
          )}
          <Box>
            <Link to={PATH.HOME} style={{ display: 'flex', alignItems: 'center' }}>
              <img
                alt="logo-min"
                className="app-logo"
                src={Logo}
                style={{ height: 36, objectFit: 'contain' }}
              />
            </Link>
          </Box>
        </Box>
        <Box
          sx={{
            flexGrow: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: '2px',
            marginRight: '12px',
          }}
        >
          <ListItem onClick={handleOpenUserMenu} sx={{ px: 2 }} disablePadding>
            <ListItemAvatar sx={{ cursor: 'pointer', minWidth: 0 }}>
              {user ? (
                <Avatar
                  {...stringAvatar(`${user}`)}
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: '0.95rem',
                    background: primary.main,
                  }}
                />
              ) : (
                <Avatar
                  sx={{ bgcolor: 'generalButton.main' }}
                  alt="No Image"
                  src={user?.imageUrl || '/assets/images/common/user.png'}
                />
              )}
            </ListItemAvatar>
          </ListItem>
          <Menu
            sx={{ mt: '20px', minWidth: 350 }}
            id="menu-appbar"
            anchorEl={anchorElUser}
            keepMounted
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
            PaperProps={{
              sx: {
                minWidth: 200,
                borderRadius: '12px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                padding: '4px',
                '& .MuiMenuItem-root': {
                  borderRadius: '8px',
                  margin: '0px 2px',
                  padding: '6px 10px',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: colorShades.veryLightGrey,
                  },
                },
              },
            }}
          >
            <MenuItem onClick={() => handleFullScreen()}>
              {!isFullscreen ? <FullscreenIcon /> : <FullscreenExitIcon />}
              <Typography textAlign="center" sx={{ ml: 1 }}>
                {!isFullscreen ? 'Full Screen' : 'Exit Full Screen'}
              </Typography>
            </MenuItem>
            {(isClient || isBoth) && (
              <MenuItem onClick={() => navigate(PATH.PROJECTS)}>
                <Payment />
                <Typography textAlign="center" sx={{ ml: 1 }}>
                  Change Project
                </Typography>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => setDialogOpen(true)}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(234, 67, 53, 0.1) !important',
                },
              }}
            >
              <LogoutIcon sx={{ fontSize: '16px', m: 0.3, color: colorShades.lightRed }} />
              <Typography textAlign="center" sx={{ ml: 1, color: colorShades.lightRed }}>
                Logout
              </Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <Dialog
        open={dialogOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            position: 'relative',
            right: '5px',
            top: '5px',
          }}
        >
          <HighlightOffIcon onClick={() => setDialogOpen(false)} sx={{ cursor: 'pointer' }} />
        </Box>
        <DialogContent
          sx={{
            padding: '14px 27px',
          }}
        >
          <DialogContentText id="alert-dialog-description">
            <Typography component="span" color={text.primary}>
              Are you sure you want to log out?
            </Typography>
          </DialogContentText>
        </DialogContent>
        <Box
          mb={3}
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'right',
            alignItems: 'center',
            paddingRight: '30px',
          }}
        >
          <CustomButton variant="outlined" onClick={() => setDialogOpen(false)}>
            No
          </CustomButton>
          <CustomButton
            color="generalButton"
            onClick={() => {
              setLogoutCheckStatus(true);
              ApplicationLogoutAPI(setLogoutStatus, logoutUser, dispatch, navigate);
            }}
          >
            Yes
          </CustomButton>
        </Box>
      </Dialog>
      <Dialog
        open={sessionDialogOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <Typography variant="h5">Session alert</Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: '14px 27px',
          }}
        >
          <DialogContentText id="alert-dialog-description">
            <Typography color={text.primary} sx={{ fontSize: 14 }}>
              This page will expire soon, Click &apos;Stay&apos; to continue.
            </Typography>
          </DialogContentText>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Typography color={text.primary} sx={{ fontSize: 14 }}>
              Expires in:
            </Typography>
            <Typography sx={{ color: 'red', fontWeight: 'bold', fontSize: 14 }}>
              {formatSecToMin(timer)}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <Box
          mb={3}
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'right',
            alignItems: 'center',
            paddingRight: '30px',
          }}
        >
          <CustomButton variant="outlined" onClick={handleStay}>
            Stay
          </CustomButton>
          <CustomButton
            color="generalButton"
            onClick={() => {
              setLogoutCheckStatus(true);
              ApplicationLogoutAPI(setLogoutStatus, logoutUser, dispatch, navigate);
            }}
          >
            Logout
          </CustomButton>
        </Box>
      </Dialog>
    </AppBar>
  );
}

export default AppHeader;
